<template>
  <FiltersItem>
    <template #title>
      {{ $l('showresult.filter.airlinesTitle') }}
    </template>
    <template #content>
      <FiltersItemContent
        v-if="isFlygresor"
      >
        <template #content>
          <!-- Toggle for blacklisted airlines -->
          <BaseToggle
            :checked="blacklistIsChecked"
            :text="$l('showresult.filter.hideBlacklistedAirlines')"
            @update:checked="toggleBlacklistIsChecked"
          />
        </template>
      </FiltersItemContent>
      <FiltersItemContent>
        <template #title>
          {{ $l('global.simplePhrases.alliances') }}
        </template>
        <template #content>
          <FiltersList
            :items="alliances"
            @itemClicked="onAllianceClicked"
            @exclusiveClicked="onAllianceExclusiveClicked"
          />
        </template>
      </FiltersItemContent>
      <FiltersItemContent>
        <template #title>
          {{ $l('showresult.filter.airlinesTitle') }}
          <span
            v-if="isAirlinesFiltered"
            class="filtersTitle__reset"
            @click="toggleAll"
          >{{ $l('showresult.filter.resetTitle') }}</span>
        </template>
        <template #content>
          <FiltersList
            id="airline-filter"
            :items="items"
            @itemClicked="onItemClicked"
            @exclusiveClicked="filterExclusive"
          />
        </template>
      </FiltersItemContent>
    </template>
    <template #summary>
      <FiltersItemSummary :items="summaryItems" />
    </template>
  </FiltersItem>
</template>

<script setup>
import { arrayToggleValue, lreplace } from '@js/utils/toolbox';
import { useFiltersStore } from './store/filtersStore';
import { useResultQueryStore } from './store/resultQueryStore';

const filtersStore = useFiltersStore();
const resultQueryStore = useResultQueryStore();

const isFlygresor = js_params.domain === 'flygresor.se';

const { summaryItems } = useSummary();
function useSummary () {
	const summaryItems = computed(() => {
		return [
			{ filterKey: 'enabledAirlines', data: filtersStore.state.enabledAirlines, prefixText: 'showresult.filter.summary.excludeAirlines' }
		]
			.map((item) => {
				const { filterKey, data, prefixText } = item;
				// If the filter has been set
				if (!filtersStore.state?.[filterKey]?.length) {
					return null;
				}

				return lreplace(prefixText, { airlines: data.map(airline => resultQueryStore.state.response.airlineDetails[airline]).join(', ') });
			})
			.filter(item => item !== null);
	});

	return {
		summaryItems
	};
}

const noAirlinesSelected = computed(() => filtersStore.state.enabledAirlines.length === 0);


// Blacklisted airlines
const { isChecked: blacklistIsChecked, toggleIsChecked: toggleBlacklistIsChecked } = useBlacklisted();
function useBlacklisted () {
	// all blacklisted airlines that actually appear in the current results
	const blacklistAirlinesInResults = computed(() => resultQueryStore.state.response?.blacklistedAirlines?.filter(airline => resultQueryStore.state.response.airlineDetails[airline]) || []);

	// true if none of those blacklisted airlines are currently enabled AND there is at least one airline enabled
	const isChecked = computed(() => blacklistAirlinesInResults.value.every(airline => !filtersStore.state.enabledAirlines.includes(airline)) && filtersStore.state.enabledAirlines.length > 0);

	function toggleIsChecked () {
		// start from full list of airlines in this result set
		let enabledAirlines = Object.keys(resultQueryStore.state.response.airlineDetails);

		if (!isChecked.value) {
			enabledAirlines = enabledAirlines.filter(airline => !blacklistAirlinesInResults.value.includes(airline));
		}

		// if you’ve ended up with *all* airlines selected, clear the filter entirely
		if (enabledAirlines.length === Object.keys(resultQueryStore.state.response.airlineDetails).length) {
			enabledAirlines = [];
		}

		filtersStore.set({ key: 'enabledAirlines', value: enabledAirlines });
	}

	return {
		isChecked,
		toggleIsChecked
	};
}

// Filter alliances
const { items: alliances, onAllianceClicked, onAllianceExclusiveClicked } = useAlliance();
function useAlliance () {
	// uniqueAlliances

	const alliances = computed(() => {
		const alliances = {};
		Object.entries(resultQueryStore.state.response?.alliance || {}).forEach(([key, value]) => {
			if (!alliances[value]) {
				alliances[value] = [];
			}
			alliances[value].push(key);
		});

		return alliances;
	});


	const items = computed(() => {
		return Object.entries(alliances.value).map(([text, values]) => {
			return {
				// Set first letter to uppercase and "alliance" to "Alliance"
				text: text.charAt(0).toUpperCase() + text.slice(1).replace('alliance', 'Alliance'),
				value: text,
				airlines: values,
				isChecked: filtersStore.state.enabledAirlines.length === 0 || values.every(alliance => filtersStore.state.enabledAirlines.includes(alliance))
			};
		});
	});

	function onAllianceClicked (item) {
		if (noAirlinesSelected.value) {
			const allAirlines = Object.keys(resultQueryStore.state.response.airlineDetails);
			const newAirlines = arrayToggleValue(allAirlines, item.airlines);
			filtersStore.set({ key: 'enabledAirlines', value: newAirlines });
			return;
		}

		if (item.isChecked) {
			filtersStore.setAll({
				alliances: filtersStore.state.alliances.filter(alliance => alliance !== item.value),
				enabledAirlines: filtersStore.state.enabledAirlines.filter(airline => !item.airlines.includes(airline))
			});
		} else {
			const enabledAirlines = [...filtersStore.state.enabledAirlines, ...item.airlines];
			const alliances = [...filtersStore.state.alliances, item.value];
			if (enabledAirlines.length === Object.keys(resultQueryStore.state.response.airlineDetails).length) {
				filtersStore.setAll({
					alliances: [],
					enabledAirlines: []
				});
				return;
			}
			filtersStore.setAll({
				alliances,
				enabledAirlines
			});
		}
	}

	function onAllianceExclusiveClicked (item) {
		filtersStore.set({ key: 'enabledAirlines', value: item.airlines });
	}

	return {
		items,
		onAllianceClicked,
		onAllianceExclusiveClicked
	};
}

// Filter individual airlines
const { items, filterExclusive, onItemClicked } = useAirlineFilters();
function useAirlineFilters () {
	const airlineCodesMerged = computed(() => {
		const airlines = {};
		for (const [key, value] of Object.entries(resultQueryStore.state?.response?.airlineDetails || {})) {
			if (!airlines[value]) {
				airlines[value] = [];
			}
			airlines[value].push(key);
		}
		return airlines;
	});


	const items = computed(() => {
		return Object.entries(airlineCodesMerged.value).map(([text, values]) => {
			return {
				text: text,
				value: values,
				isChecked: noAirlinesSelected.value || values.some(airline => filtersStore.state.enabledAirlines.includes(airline))
			};
		});
	});


	function filterExclusive (item) {
		const airlines = item.value;
		filtersStore.set({ key: 'enabledAirlines', value: airlines });
	}


	function onItemClicked (item) {
	// if enabledAirlines is empty then add all airlines except the one clicked
		if (noAirlinesSelected.value) {
			const airlines = Object.keys(resultQueryStore.state.response.airlineDetails).filter(airline => !item.value.includes(airline));
			filtersStore.set({ key: 'enabledAirlines', value: airlines });
		} else {
			const currentAirlines = filtersStore.state.enabledAirlines;
			// Add or remove the clicked airline
			let newAgentsFilter = [];
			if (item.isChecked) {
				newAgentsFilter = currentAirlines.filter(airline => !item.value.includes(airline));
			} else {
				newAgentsFilter = [...currentAirlines.concat(item.value)];
			}

			// If all airlines are selected then remove all airlines
			if (newAgentsFilter.length === Object.keys(resultQueryStore.state.response.airlineDetails).length) {
				newAgentsFilter = [];
			}
			filtersStore.set({ key: 'enabledAirlines', value: newAgentsFilter });
		}
	}

	return {
		items,
		filterExclusive,
		onItemClicked
	};
}

// Toggle all airlines
const { toggleAll, isAirlinesFiltered } = useAirlinesToggleAll();
function useAirlinesToggleAll () {
	function toggleAll () {
		if (noAirlinesSelected.value) {
			return;
		}
		filtersStore.set({ key: 'enabledAirlines', value: [] });
	}

	const isAirlinesFiltered = computed(() => filtersStore.state.enabledAirlines.length > 0);

	return {
		toggleAll,
		isAirlinesFiltered
	};
}

</script>
